/**
 * CompanionCallout — "{companion} · noticed something", the last element of a
 * home's hero (ui-ux-guideline §2.6 item 4 for staff, §2.9 item 3 for the
 * family, 2026-09-18). One shape for both homes:
 *
 *   - a pattern that owns a page carries `href` → primary **Take me there**,
 *     ghost **Think it through with me** (the companion with the lane's
 *     prompt). No decision happens on the home; the page does.
 *   - a pattern with no `href` (a remembrance date for staff, an appointment
 *     the companion offers to prep for a parent) keeps its own companion
 *     action primary, under its own `ctaLabel`.
 *   - more than one pattern → the pager in `CompanionCard` (§2.9 rule 6).
 *
 * The engines are pure and tested on their own (`buildWilliamsPatterns` in
 * `trfer-display.ts`, `buildParentPatterns` in `parent-home-noticing.ts`);
 * this component is presentational. It never decides where the prompt goes —
 * the caller passes `onAsk`, so the staff home opens Williams and the family
 * home opens `/chat`.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompanionCard, usePager } from './CompanionCard';

export interface CompanionPattern {
  id: string;
  body: string;
  /** The primary label when the pattern has no `href`. */
  ctaLabel: string;
  /** The prompt handed to `onAsk`. */
  prompt: string;
  /** The page that owns the decision, with its filter set. */
  href?: string;
}

interface CompanionCalloutProps {
  patterns: CompanionPattern[];
  /** Sentence case (§7): "Williams · noticed something", "Maya · noticed something". */
  label: string;
  onAsk: (prompt: string) => void;
  /** The ghost action beside Take me there. Defaults to `companion:callout.thinkItThrough`. */
  askLabel?: string;
  layout?: 'inline' | 'card';
  className?: string;
  'data-testid'?: string;
}

export function CompanionCallout({
  patterns,
  label,
  onAsk,
  askLabel,
  layout = 'inline',
  className,
  ...rest
}: CompanionCalloutProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('companion');
  const { index, pager } = usePager(patterns.length);

  if (patterns.length === 0) return null;
  const current = patterns[index];

  return (
    <CompanionCard
      label={label}
      body={current.body}
      layout={layout}
      pager={pager}
      className={className}
      data-testid={rest['data-testid']}
    >
      {current.href ? (
        <>
          <Button size="sm" variant="brand" className="h-8 text-caption" onClick={() => navigate(current.href!)}>
            {t('callout.takeMeThere')}
            <ArrowRight className="h-3 w-3 ml-1" aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onAsk(current.prompt)}
            className="h-8 text-caption text-stevens-indigo"
          >
            <Sparkles className="h-3 w-3 mr-1" aria-hidden />
            {askLabel ?? t('callout.thinkItThrough')}
          </Button>
        </>
      ) : (
        <Button size="sm" onClick={() => onAsk(current.prompt)} variant="brand" className="h-8 text-caption">
          <Sparkles className="h-3 w-3 mr-1" aria-hidden />
          {current.ctaLabel}
        </Button>
      )}
    </CompanionCard>
  );
}
